import React, { useMemo, useState } from "react";
import { IGif, INiche, IUser } from "services/types";
import { MetaInfo } from "../MetaInfo";
import { ViewRegion } from "../../../utils/metrics";
import { CurrentUserObject } from "hooks/useVisitorInfo";
import { LikeSoundOverlay } from "../LikeSoundOverlay";
import { Sidebar } from "../Sidebar";
import { MenuShareEvent } from "utils/bus";
import { Video } from "../Video";
import { ImageGif } from "../ImageGif";
import { FullScreenNav } from "../FullScreenNav";
import { GalleryGif } from "../GalleryGif";
import { clsx } from "clsx";
import "./Player.scss";

enum GifType {
  Image = 2,
  Video = 1
}

interface IPlayerProps {
  user: IUser | undefined;
  embedded?: boolean;
  niches: INiche[];
  rel?: string;
  position: number;
  source: ViewRegion;
  sourceId: string | null;
  playerWrapRef: React.RefObject<HTMLDivElement>;
  gifQuality: string;
  gifSound: boolean;
  fullscreen: boolean;
  gif: IGif;
  currentUser: CurrentUserObject | null;
  liked: boolean;
  likesCount: number;
  isRotate: boolean;
  isHLS: boolean;
  isVisible: boolean;
  isActive: boolean;
  followingIds: string[];
  onFollow: () => void;
  isDismissScrollMore: () => boolean;
  onDismissScrollMore: () => void;
  onMenu: (options: MenuShareEvent) => void;
  onShare: (options: MenuShareEvent) => void;
  onLike: () => void;
  onFullScreen: () => void;
  onSound: (sound: boolean) => void;
  onQuality: (quality: string) => void;
  onViewMetric: () => void;
  viewMetricQuery: (gif: IGif) => void;
  onProfileMetric: (value: string) => void;
  onVideoLoadMetric: (duration: number) => void;
}

export const Player: React.FC<IPlayerProps> = (props) => {
  const {
    sourceId,
    niches,
    playerWrapRef,
    currentUser,
    gifQuality,
    gifSound,
    fullscreen,
    gif,
    liked,
    likesCount,
    isRotate,
    isHLS,
    isVisible,
    isActive,
    isDismissScrollMore,
    position,
    followingIds,
    onLike,
    onSound,
    onFullScreen,
    onQuality,
    onMenu,
    onShare,
    onViewMetric,
    viewMetricQuery,
    onProfileMetric,
    onDismissScrollMore,
    onVideoLoadMetric,
    onFollow
  } = props;

  const [currentGalleryGif, setCurrentGalleryGif] = useState<IGif | null>(gif.gallery ? gif : null);

  // Props for the wrapper div.
  const attrs = useMemo(() => {
    const ratio = gif.width / gif.height;
    const height = ratio < 1.0 ? gif.height : gif.width;

    return {
      id: `gif_${gif.id}`,
      className: clsx({
        "Player": true,
        "Player_isActive": isVisible,
        "Player_isFullscreen": fullscreen,
        "Player_isGallery": !!gif.gallery,
        "Player_isImage": isActive && gif.type === GifType.Image,
        "Player_isVideo": isActive && gif.type === GifType.Video,
        "Player_isRotate": isRotate
      }),
      style: {
        aspectRatio: fullscreen ? undefined : `${gif.width} / ${height}`,
        height: fullscreen ? `${window.innerHeight}px` : "100%",
      },
      ref: playerWrapRef,
    };
  }, [gif, isVisible, fullscreen, isActive, playerWrapRef, isRotate]);

  // Just a placeholder for gifs off screen, needed to maintain the scroll position.
  if (!isActive) {
    return <div {...attrs}></div>;
  }

  return (
    <div {...attrs}>
      {fullscreen && <FullScreenNav toggleFullScreen={onFullScreen} />}

      <div className="Player-BackdropWrap">
        <img
          // @ts-ignore
          fetchpriority={position === 1 ? "high" : undefined}
          src={gif.urls.thumbnail}
          alt={gif.id}
        />
      </div>

      {gif.type === GifType.Video &&
        (
          <Video
            poster={gif.urls.thumbnail}
            position={position}
            gif={gif}
            fullscreen={fullscreen}
            sound={gifSound}
            handleLike={onLike}
            context={sourceId}
            isVisible={isVisible}
            gifQuality={gifQuality}
            isHLS={isHLS}
            isDismissScrollMore={isDismissScrollMore}
            onSound={onSound}
            onViewMetric={onViewMetric}
            onDismissScrollMore={onDismissScrollMore}
            onVideoLoadMetric={onVideoLoadMetric}
          />
        )
      }

      {gif.type === GifType.Image && (
        gif.gallery ? (
          <GalleryGif
            galleryId={gif.gallery}
            initialGif={gif}
            setCurrentGalleryGif={setCurrentGalleryGif}
            onLike={onLike}
            viewMetricQuery={viewMetricQuery}
          />
        ) : (
          <ImageGif
            onLike={onLike}
            gif={gif}
            fullscreen={fullscreen}
          />
        )
      )}

      <div className="Player-SideBarWrap">
        <Sidebar
          onMenu={onMenu}
          onShare={onShare}
          onLike={onLike}
          onFullScreen={onFullScreen}
          onSound={onSound}
          onQuality={onQuality}
          gifSound={gifSound}
          gifQuality={gifQuality}
          gif={currentGalleryGif !== null ? currentGalleryGif : gif}
          currentUserName={currentUser?.username}
          liked={liked}
          likesCount={likesCount}
          fullscreen={fullscreen}
        />
      </div>

      <div className="Player-MetaInfo" onContextMenu={event => event.preventDefault()}>
        <MetaInfo
          followingIds={followingIds}
          gif={currentGalleryGif !== null ? currentGalleryGif : gif}
          user={props.user}
          niches={niches}
          currentUser={currentUser}
          rel={props.rel}
          onFollow={onFollow}
          onProfileMetric={onProfileMetric}
        />
      </div>
      <LikeSoundOverlay />
    </div>
  );
};
